import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Copy, X } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUPON_KEY, useCart } from "@/context/CartContext";

/** Lazy so `/` and `/products` never download marsidev / CF api.js until email-capture is open. */
const SafeTurnstile = lazy(() =>
  import("@/components/SafeTurnstile").then((m) => ({ default: m.SafeTurnstile }))
);

const SEEN_KEY = (id) => `pokeforge_popup_${id}`;
const VIEWS_KEY = "pokeforge_page_views";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

/** Pages that already mount SafeTurnstile — never auto-open email_capture here. */
const TURNSTILE_OWN_PATHS = new Set(["/waitlist", "/login", "/register"]);

const suppressed = (popup) => {
  const until = Number(localStorage.getItem(SEEN_KEY(popup.id)) || 0);
  return until > Date.now();
};

const suppress = (popup) =>
  localStorage.setItem(SEEN_KEY(popup.id), String(Date.now() + popup.suppress_days * 86400000));

export const PopupManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, coupon, applyCoupon } = useCart();
  const [popups, setPopups] = useState([]);
  const [active, setActive] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState(null);
  const [copied, setCopied] = useState(false);
  const [token, setToken] = useState("");
  const [captchaBroken, setCaptchaBroken] = useState(false);
  /** Email-capture dialog is open, but Turnstile mounts only after paint/settle. */
  const [captchaMount, setCaptchaMount] = useState(false);
  const turnstileRef = useRef(null);

  useEffect(() => {
    api
      .get("/popups")
      .then(({ data }) => setPopups(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const close = useCallback(() => {
    if (active) suppress(active);
    setActive(null);
    setOffer(null);
    setToken("");
    setCaptchaBroken(false);
    setCaptchaMount(false);
  }, [active]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  // Page view counter for page_views triggers.
  useEffect(() => {
    const views = Number(sessionStorage.getItem(VIEWS_KEY) || 0) + 1;
    sessionStorage.setItem(VIEWS_KEY, String(views));
  }, [location.pathname]);

  // Drop an open email-capture popup when entering a page that has its own Turnstile
  // so only one widget is mounted (do not suppress — user may see it again elsewhere).
  useEffect(() => {
    if (!active || active.type !== "email_capture") return;
    if (!TURNSTILE_OWN_PATHS.has(location.pathname)) return;
    setActive(null);
    setOffer(null);
    setToken("");
    setCaptchaBroken(false);
    setCaptchaMount(false);
  }, [location.pathname, active]);

  // Open the dialog first; only then (after a settle) mount Turnstile so CF never
  // injects while the shell chrome is still visibility/opacity-transitioning.
  useEffect(() => {
    if (!active || active.type !== "email_capture") {
      setCaptchaMount(false);
      return;
    }
    setCaptchaMount(false);
    const settleMs = location.pathname === "/" ? 400 : 200;
    const t = window.setTimeout(() => setCaptchaMount(true), settleMs);
    return () => {
      window.clearTimeout(t);
      setCaptchaMount(false);
    };
  }, [active, location.pathname]);

  const eligible = useCallback(
    (popup) => {
      if (suppressed(popup)) return false;
      if (popup.paths?.length && !popup.paths.includes(location.pathname)) return false;
      // Dual Turnstile (page widget + popup) → ParityDigital crash. Suppress capture popups
      // on routes that already render SafeTurnstile (waitlist / login / register).
      if (popup.type === "email_capture" && TURNSTILE_OWN_PATHS.has(location.pathname)) {
        return false;
      }
      return true;
    },
    [location.pathname]
  );

  // Delay / page-view triggers.
  // Home: add a short settle so email_capture Turnstile never races another widget
  // or the vault bumper handoff (ParityDigital from dual mounts).
  useEffect(() => {
    if (active) return;
    const timers = [];
    const homeSettleMs = location.pathname === "/" ? 1500 : 0;
    popups
      .filter((p) => p.trigger !== "exit_checkout" && eligible(p))
      .forEach((p) => {
        if (p.trigger === "page_views") {
          const views = Number(sessionStorage.getItem(VIEWS_KEY) || 0);
          if (views >= p.page_views) {
            timers.push(setTimeout(() => setActive((cur) => cur || p), homeSettleMs));
          }
          return;
        }
        const ms = Math.max((p.delay_seconds || 0) * 1000, homeSettleMs);
        timers.push(setTimeout(() => setActive((cur) => cur || p), ms));
      });
    return () => timers.forEach(clearTimeout);
  }, [popups, eligible, active, location.pathname]);

  // Abandoned checkout: they were on /checkout with items and left without paying.
  const left = location.pathname !== "/checkout" && !location.pathname.startsWith("/payment");
  useEffect(() => {
    if (active) return;
    const wasOnCheckout = sessionStorage.getItem("pokeforge_on_checkout") === "1";
    if (location.pathname === "/checkout") {
      sessionStorage.setItem("pokeforge_on_checkout", "1");
      return;
    }
    if (location.pathname.startsWith("/payment")) {
      sessionStorage.removeItem("pokeforge_on_checkout");
      return;
    }
    // localStorage is checked as well: after a hard reload the coupon is re-priced async.
    if (!wasOnCheckout || !left || items.length === 0 || coupon) return;
    if (localStorage.getItem(COUPON_KEY)) return;
    const popup = popups.find((p) => p.trigger === "exit_checkout" && eligible(p));
    if (!popup) return;
    sessionStorage.removeItem("pokeforge_on_checkout");
    api
      .post(`/popups/${popup.id}/offer`)
      .then(({ data }) => {
        setOffer(data);
        setActive(popup);
      })
      .catch(() => {});
  }, [location.pathname, popups, items.length, coupon, eligible, active, left]);

  const resetCaptcha = () => {
    setToken("");
    try {
      turnstileRef.current?.reset?.();
    } catch (_) {
      /* ignore */
    }
  };

  const join = async (e) => {
    e.preventDefault();
    if (!token && !captchaBroken && SITE_KEY) {
      toast.error("Complete the verification first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/popups/${active.id}/submit`, {
        email,
        name,
        turnstile_token: token || "captcha-unavailable",
      });
      toast.success(data.message);
      suppress(active);
      setActive(null);
      setToken("");
      setCaptchaBroken(false);
    } catch (err) {
      toast.error(apiError(err));
      resetCaptcha();
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(offer.code);
    setCopied(true);
    toast.success("Code copied");
  };

  const applyAndReturn = async () => {
    setBusy(true);
    const ok = await applyCoupon(offer.code);
    setBusy(false);
    suppress(active);
    setActive(null);
    navigate(ok ? "/checkout" : `/checkout?code=${offer.code}`);
  };

  if (!active) return null;

  const field =
    "w-full bg-[#050505] px-3 py-3 font-mono text-xs text-white outline-none ring-1 ring-zinc-800 focus:ring-[#00ffcc]";

  return (
    <div
      data-testid="site-popup"
      className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-sm animate-in border border-[#00ffcc]/40 bg-[#0a0a0a] p-6 shadow-[0_0_50px_-10px_rgba(0,255,204,0.45)] sm:left-auto sm:right-6 sm:bottom-6"
    >
      <button
        data-testid="popup-close-btn"
        onClick={close}
        aria-label="Close"
        className="absolute right-3 top-3 text-zinc-600 transition-colors hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>

      <p className="font-display text-lg leading-tight tracking-tight text-white">{active.title}</p>
      {active.body && (
        <p data-testid="popup-body" className="mt-3 text-xs leading-relaxed text-zinc-400">
          {active.body}
        </p>
      )}

      {active.type === "discount_offer" && offer && (
        <div className="mt-5">
          <div className="flex items-center justify-between gap-3 border border-[#00ffcc]/40 bg-[#00ffcc]/[0.06] px-4 py-3">
            <span data-testid="popup-coupon-code" className="font-display text-xl text-[#00ffcc]">
              {offer.code}
            </span>
            <button
              data-testid="popup-copy-code-btn"
              onClick={copy}
              className="text-zinc-400 transition-colors hover:text-[#00ffcc]"
              aria-label="Copy code"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            {offer.percent_off}% off · Event Passes excluded
          </p>
          <button
            data-testid="popup-apply-code-btn"
            disabled={busy}
            onClick={applyAndReturn}
            className="mt-4 w-full border border-[#00ffcc] py-3 text-[10px] uppercase tracking-[0.25em] text-[#00ffcc] transition-colors hover:bg-[#00ffcc] hover:text-black disabled:opacity-40"
          >
            {busy ? "Applying…" : active.button_label}
          </button>
        </div>
      )}

      {active.type === "email_capture" && (
        <form onSubmit={join} className="mt-5 space-y-3">
          {active.collect_name && (
            <input
              data-testid="popup-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First name"
              className={field}
            />
          )}
          <input
            data-testid="popup-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className={field}
            required
          />
          {/* CF script + widget only after dialog open AND captchaMount settle — never on bare `/` or `/products`. */}
          <div className="min-h-[65px]" data-testid="popup-turnstile">
            {SITE_KEY ? (
              captchaMount ? (
                <Suspense
                  fallback={
                    <div
                      data-testid="popup-turnstile-loading"
                      className="min-h-[65px] w-full max-w-[300px]"
                      aria-hidden
                    />
                  }
                >
                  <SafeTurnstile
                    key={`promo-popup-${active.id}`}
                    ref={turnstileRef}
                    id="cf-turnstile-promo-popup"
                    siteKey={SITE_KEY}
                    onSuccess={(t) => {
                      setToken(t);
                      setCaptchaBroken(false);
                    }}
                    onExpire={resetCaptcha}
                    onUnsupported={() => {
                      setCaptchaBroken(true);
                      setToken("");
                    }}
                    onUnavailable={() => {
                      setCaptchaBroken(true);
                      setToken("");
                    }}
                    options={{ theme: "dark", size: "normal", action: "promo_popup" }}
                  />
                </Suspense>
              ) : (
                <div
                  data-testid="popup-turnstile-deferred"
                  className="min-h-[65px] w-full max-w-[300px]"
                  aria-hidden
                />
              )
            ) : (
              <p className="text-[10px] text-[#ff3b30]">CAPTCHA is not configured.</p>
            )}
            {captchaBroken && (
              <p className="mt-2 text-[10px] leading-relaxed text-zinc-500">
                Verification could not load on this domain — the server still checks the token.
              </p>
            )}
          </div>
          <button
            data-testid="popup-submit-btn"
            disabled={busy || !SITE_KEY}
            className="w-full border border-[#00ffcc] py-3 text-[10px] uppercase tracking-[0.25em] text-[#00ffcc] transition-colors hover:bg-[#00ffcc] hover:text-black disabled:opacity-40"
          >
            {busy ? "Adding…" : active.button_label}
          </button>
        </form>
      )}

      <button
        data-testid="popup-dismiss-btn"
        onClick={close}
        className="mt-3 w-full text-[10px] uppercase tracking-[0.25em] text-zinc-600 transition-colors hover:text-zinc-300"
      >
        {active.dismiss_label}
      </button>
    </div>
  );
};
